import { type FC, type ReactNode, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import Grid from "@mui/material/Grid";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { DateTimePicker, Switch } from "@/shared/components/Form";
import type { PlayerTimecode } from "@/shared/components/Player";
import { ShareUrl } from "@/shared/components/ShareUrl";
import { useIsMobile } from "@/shared/hooks";
import type { VideoFormValues } from "../model";
import { VideoUploadField } from "./VideoUploadField";
import { PreviewImageField } from "./PreviewImageField";
import { PlayerPreview } from "./PlayerPreview";
import { TagsByTypeField } from "./TagsByTypeField";
import { CountryAccessField } from "./CountryAccessField";
import { WidgetField } from "./WidgetField";
import { CollectionsField } from "@/partner/features/collections";
import { ProductsField } from "@/partner/features/products";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import { EmbedCode } from "./EmbedCode";
import { VideoDownload } from "./VideoDownload";

const FORM_ID = "content-form";
// Совпадает с SPACING/FORM_SPACING референсного VideoForm/Edit/Edit.js
const SPACING = 1;
const FORM_SPACING = 2;

interface VideoFormMetadata {
  /** Id контента: включает кнопку «Скачать» в блоке шаринга (только редактирование). */
  contentId?: string | null;
  url?: string | null;
  embedUrl?: string | null;
  hlsUrl?: string | null;
  processingStatus?: string | null;
  thumbnailUrl?: string | null;
  transcodingError?: string | null;
}

interface VideoFormProps {
  title: string;
  loading?: boolean;
  initialValues?: Partial<VideoFormValues>;
  onSubmit: (values: VideoFormValues) => Promise<void>;
  onClose: () => void;
  metadata?: VideoFormMetadata;
  /** См. FormBuilder.keepDirtyOnReinitialize — прокидывается напрямую в react-final-form Form */
  keepDirtyOnReinitialize?: boolean;
  /** Пробрасывается наверх из VideoUploadField вместе с его abort() — родитель
   * может, например, гарантированно оборвать загрузку при подтверждённом
   * закрытии формы (см. EditVideoDialog), а не только заблокировать onClose. */
  onUploadingChange?: (uploading: boolean, abortUpload: () => void) => void;
  /** Вкладки формы видео (VideoTabsMenu) — рендерятся под шапкой, как
   * референсный VideoForm/Menu над контентом активного таба. */
  menu?: ReactNode;
  /** Сохранённые метки таймлайна — показываются в превью-плеере формы. */
  timecodes?: PlayerTimecode[] | null;
}

export const VideoForm: FC<VideoFormProps> = ({
  title,
  loading = false,
  initialValues = {},
  onSubmit,
  onClose,
  metadata,
  keepDirtyOnReinitialize,
  onUploadingChange,
  menu,
  timecodes,
}) => {
  const { t } = useTranslation();
  // Совпадает с референсным useIsMobile("sm") — переворот колонок на мобильном
  // происходит на брейкпоинте 600px, а не на дефолтных 768px хука.
  const isMobile = useIsMobile("(max-width: 600px)");
  const { data: platformData } = useCurrentPlatformQuery();
  const isVideoChatEnabled = Boolean(
    platformData?.currentPlatform?.isVideoChatEnabled
  );
  const isDraft = !initialValues.publishAt;
  // true = «Опубликовать» (publishAt=now, если пусто), false = «Сохранить черновик»/«Скрыть».
  // Устанавливается синхронно в onClick одной из двух submit-кнопок непосредственно
  // перед сабмитом формы (см. DialogHeader ниже).
  const publishIntentRef = useRef(!isDraft);
  // Идёт ли сейчас загрузка видео в VideoUploadField — блокирует обе
  // submit-кнопки (см. референсный Edit.js: disabled={loading||isActiveUpload}).
  const [isUploading, setIsUploading] = useState(false);
  const handleUploadingChange = (
    uploading: boolean,
    abortUpload: () => void
  ) => {
    setIsUploading(uploading);
    onUploadingChange?.(uploading, abortUpload);
  };

  const handleFormSubmit = async (values: VideoFormValues) => {
    const publish = publishIntentRef.current;
    await onSubmit({
      ...values,
      publishAt: publish ? values.publishAt || new Date().toISOString() : null,
    });
  };

  return (
    <Form<VideoFormValues>
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      keepDirtyOnReinitialize={keepDirtyOnReinitialize}
      subscription={{ values: true }}
      render={({ handleSubmit, values }) => (
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <AppBar color="default" position="fixed" elevation={0}>
            <Toolbar>
              <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                {title}
              </Typography>
              <Stack direction="row" spacing={1}>
                {/* Кнопку публикации не блокируем, пока видео обрабатывается:
                    запрет мешал бы партнёрам, планирующим дату публикации
                    заранее (загрузили сейчас, публикация выставлена на
                    завтра, к сроку видео почти наверняка уже готово). Вместо
                    запрета ниже показано явное предупреждение
                    (processingWarningAlert), что до конца обработки видео
                    не появится на витрине, даже если контент опубликован. */}
                <Tooltip
                  title={
                    isDraft
                      ? t("videos.form.publishTooltip")
                      : t("videos.form.saveChangesTooltip")
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      type="submit"
                      form={FORM_ID}
                      disabled={loading || isUploading}
                      onClick={() => {
                        publishIntentRef.current = true;
                      }}
                    >
                      {isDraft
                        ? t("common.actions.publish")
                        : t("common.actions.save")}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    isDraft
                      ? t("videos.form.saveDraftTooltip")
                      : t("videos.form.hideTooltip")
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      type="submit"
                      form={FORM_ID}
                      disabled={loading || isUploading}
                      onClick={() => {
                        publishIntentRef.current = false;
                      }}
                    >
                      {isDraft
                        ? t("videos.form.saveDraftButton")
                        : t("common.actions.hide")}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Toolbar>
            <Divider />
          </AppBar>

          <Box sx={{ mt: { xs: "56px", sm: "64px" } }}>{menu}</Box>

          <Box sx={{ p: 1 }}>
            <Grid
              container
              spacing={SPACING}
              flexDirection={isMobile ? "column-reverse" : "row"}
            >
              <Grid size={{ xs: 12, sm: 6, md: 7 }}>
                <Grid container spacing={SPACING}>
                  <Grid size={12}>
                    <Card elevation={0}>
                      <CardContent sx={{ pt: 3 }}>
                        <Grid container spacing={FORM_SPACING}>
                          <Grid size={12}>
                            <Field name="title">
                              {({ input, meta }) => (
                                <TextField
                                  {...input}
                                  autoFocus
                                  required
                                  label={t("videos.form.titleLabel")}
                                  fullWidth
                                  variant="outlined"
                                  error={meta.touched && Boolean(meta.error)}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid size={12}>
                            <Field name="description">
                              {({ input }) => (
                                <TextField
                                  {...input}
                                  label={t("videos.form.descriptionLabel")}
                                  multiline
                                  minRows={2}
                                  maxRows={10}
                                  fullWidth
                                  variant="outlined"
                                />
                              )}
                            </Field>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={12}>
                    <Card elevation={0}>
                      <CardContent sx={{ pt: 3 }}>
                        <Grid container spacing={FORM_SPACING}>
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <VideoUploadField
                              onUploadingChange={handleUploadingChange}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <PreviewImageField />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={12}>
                    <Card elevation={0}>
                      <CardContent sx={{ pt: 3 }}>
                        <Grid container spacing={FORM_SPACING}>
                          <Grid size={12}>
                            <TagsByTypeField />
                          </Grid>
                          <Grid size={12}>
                            <ProductsField />
                          </Grid>
                          <Grid size={12}>
                            <CollectionsField />
                          </Grid>
                          {metadata?.transcodingError && (
                            <Grid size={12}>
                              <Alert variant="filled" severity="error">
                                {metadata.transcodingError}
                              </Alert>
                            </Grid>
                          )}
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <DateTimePicker
                              name="publishAt"
                              label={t("videos.form.publishAtLabel")}
                              outputFormat="iso"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, lg: 6 }}>
                            <DateTimePicker
                              name="accessStartAt"
                              label={t("videos.form.accessStartAtLabel")}
                              outputFormat="iso"
                            />
                          </Grid>
                          <Grid size={12}>
                            <CountryAccessField />
                          </Grid>
                          <Grid size={12}>
                            <WidgetField />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={12}>
                    <Card elevation={0}>
                      <CardContent sx={{ pt: 2 }}>
                        <FormControl component="fieldset">
                          <FormGroup>
                            <Switch
                              name="hideInGeneralLists"
                              label={t("videos.form.hideInGeneralListsLabel")}
                            />
                            <Switch
                              name="onlyUsers"
                              label={t("videos.form.onlyUsersLabel")}
                            />
                            {isVideoChatEnabled && (
                              <Switch
                                name="isChatEnabled"
                                label={t("videos.form.isChatEnabledLabel")}
                              />
                            )}
                          </FormGroup>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                <Grid container spacing={SPACING}>
                  <Grid size={12}>
                    <Card elevation={0}>
                      <PlayerPreview
                        hlsUrl={metadata?.hlsUrl}
                        processingStatus={metadata?.processingStatus}
                        thumbnailUrl={metadata?.thumbnailUrl}
                        hasPreview={Boolean(values?.previewId)}
                        timecodes={timecodes}
                      />
                    </Card>
                  </Grid>
                  <Grid size={12}>
                    <Card elevation={0}>
                      <CardContent>
                        <Stack spacing={2}>
                          {metadata?.processingStatus === "PROCESSING" && (
                            <Alert severity="warning" variant="outlined">
                              {t("videos.form.processingWarningAlert")}
                            </Alert>
                          )}
                          <Alert severity="info" variant="outlined">
                            {values?.publishAt
                              ? t("videos.form.availableInfoAlert")
                              : t("videos.form.notYetAvailableInfoAlert")}
                          </Alert>
                          <ShareUrl
                            url={metadata?.url}
                            label={t("platformCustomCovers.form.link")}
                            copiedMessage={t("products.form.shareUrlCopied")}
                          />
                          <EmbedCode embedUrl={metadata?.embedUrl} />
                          {metadata?.contentId &&
                            metadata.processingStatus === "COMPLETE" && (
                              <VideoDownload contentId={metadata.contentId} />
                            )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      )}
    />
  );
};
